/**
 * ESEIS-25 Client-Side Scoring Engine
 * Mirrors Python webhook logic for instant results display
 * 
 * Usage: Include on Shopify results page
 * Typeform redirects with answers in URL params
 */

// =============================================================================
// LABEL TO CODE MAPPINGS
// =============================================================================

const LABEL_TO_CODE = {
  // Q1: Age Group
  '30s': '30s',
  '40s': '40s',
  '50s': '50s',
  '60+': '60plus',
  
  // Q2: Energy Answer
  'Steady throughout the day': 'steady',
  'Could use a boost to stay sharp': 'needs_boost',
  "Noticeably lower than I'd like": 'low',
  
  // Q3: Sleep Answer
  'I wake up feeling rested': 'rested',
  'I occasionally have trouble falling asleep': 'trouble_falling_asleep',
  'I sometimes wake during the night': 'wake_during_night',
  "I sleep but don't always feel restored": 'not_restored',
  
  // Q4: Stress Answer
  'I manage demands well and recover quickly': 'manages_well',
  'I notice occasional tension or irritability': 'occasional_tension',
  "I feel like I'm running on empty more often than I'd like": 'running_on_empty',
  "I could use support for my body's stress response": 'needs_support',
  
  // Q5: Metabolism Answer
  'My weight is stable and digestion feels comfortable': 'stable',
  "I've noticed gradual changes in my midsection": 'midsection_changes',
  'I experience occasional digestive discomfort': 'digestive_discomfort',
  'I notice energy dips between meals': 'energy_dips',
  'More than one of the above': 'multiple_issues',
  
  // Q6: Exercise Answer
  'I train regularly and recover well': 'trains_regularly',
  'I\'m active but recovery takes longer than it used to': 'slow_recovery',
  "I'm less consistent than I'd like to be": 'inconsistent',
  'I\'d like to be more active but energy is a limiting factor': 'energy_limited',
  
  // Q7: Wellness Signals (multi-select)
  'Changes in drive or motivation': 'drive_changes',
  'Gradual shifts in body composition': 'body_composition',
  'Occasional mental fogginess': 'mental_fogginess',
  'Feeling cold more easily': 'feeling_cold',
  'Less physical stamina than before': 'less_stamina',
  'None of these': 'none',
  
  // Q8: Contraindications (multi-select)
  'Blood pressure support': 'blood_pressure',
  'Blood sugar management': 'blood_sugar',
  'Thyroid support': 'thyroid',
  'Cholesterol management': 'cholesterol',
  'Cardiovascular support': 'cardiovascular',
  'Mood or stress support (under medical guidance)': 'mood',
  'None of the above': 'none',
  
  // Q10: Priority Answer
  'Steady, sustainable energy': 'energy',
  'Restful, restorative sleep': 'sleep',
  'Physical strength and recovery': 'strength',
  'Metabolic efficiency': 'metabolism',
  'Mental clarity and focus': 'clarity',

  // NEW Q1: Energy Answer (GHL survey v2 display text)
  'Solid. Still running at full capacity.': 'steady',
  'Manageable. Coffee helps.': 'needs_boost',
  'Running on fumes. Powering through.': 'low',
  'Done. Just trying to make it to dinner.': 'low',

  // NEW Q2: Sleep Answer (GHL survey v2 display text)
  'Ready. Actually rested.': 'rested',
  'Okay. Takes a few minutes to get going.': 'trouble_falling_asleep',
  "Tired. Like I didn't really sleep.": 'not_restored',
  'Wrecked. Every single morning.': 'not_restored',

  // NEW Q3: Metabolism Answer (GHL survey v2 display text)
  'Stayed solid. No real changes.': 'stable',
  'Shifted a little. Nothing dramatic.': 'midsection_changes',
  'Changed noticeably. Especially the midsection.': 'midsection_changes',
  'Changed a lot. I barely recognize my body.': 'multiple_issues',

  // NEW Q4: Stress Answer (GHL survey v2 display text)
  "Handle it. That's what I do.": 'manages_well',
  'Handle it — but it takes more out of me than it used to.': 'occasional_tension',
  'Struggle. The recovery time has gotten longer.': 'running_on_empty',
  'White-knuckle it. Running on empty most days.': 'needs_support',

  // NEW Q5: Exercise Answer (GHL survey v2 display text)
  'Consistent. I train and I bounce back.': 'trains_regularly',
  'Active, but recovery takes longer than it used to.': 'slow_recovery',
  'Less consistent than I want to be.': 'inconsistent',
  "Low energy is the main reason I'm not doing more.": 'energy_limited',

  // NEW Q6: Wellness Signals (GHL survey v2 display text)
  'Less drive or motivation than before.': 'drive_changes',
  'Body changing despite consistent habits.': 'body_composition',
  'Foggy or slower mentally than I used to be.': 'mental_fogginess',
  'Feel cold more easily than before.': 'feeling_cold',
  'Less physical stamina or endurance.': 'less_stamina',
  'None of these apply to me.': 'none',

  // NEW Q7: Contraindications (GHL survey v2 display text)
  'Blood pressure': 'blood_pressure',
  'Blood sugar management': 'blood_sugar',
  'Thyroid health': 'thyroid',
  'Cholesterol management': 'cholesterol',
  'Cardiovascular health': 'cardiovascular',
  'None of the above': 'none',

  // NEW Q8: Priority Answer (GHL survey v2 display text)
  'Energy and focus during the day.': 'energy',
  'Sleep quality and recovery.': 'sleep',
  'Physical strength and body composition.': 'strength',
  'Mental sharpness and stress response.': 'clarity'
};

// =============================================================================
// PACK DEFINITIONS
// =============================================================================

const PACK_DEFINITIONS = [
  { name: 'Adrenal Support', slug: 'adrenal-pack', price: 79, scoreKey: 'adrenal' },
  { name: 'Energy Support', slug: 'energy-pack', price: 87, scoreKey: 'energy' },
  { name: 'Sleep Support', slug: 'sleep-pack', price: 59, scoreKey: 'sleep' },
  { name: 'Metabolic Support', slug: 'blood-sugar-pack', price: 87, scoreKey: 'blood_sugar' },
  { name: 'Digestive Wellness', slug: 'gut-pack', price: 83, scoreKey: 'gut' },
  { name: 'Calm & Balance', slug: 'mood-pack', price: 73, scoreKey: 'mood' },
  { name: "Men's Vitality", slug: 'mens-hormone-pack', price: 64, scoreKey: 'mens_hormone' },
  { name: 'Performance', slug: 'performance-pack', price: 97, scoreKey: 'performance' },
  { name: 'Muscle Preserve', slug: 'muscle-pack', price: 59, scoreKey: 'muscle' },
  { name: 'Metabolic Energy', slug: 'thyroid-pack', price: 90, scoreKey: 'thyroid' },
  { name: 'Cognitive Support', slug: 'brain-pack', price: 68, scoreKey: 'brain' },
  { name: 'Focus Support', slug: 'focus-pack', price: 70, scoreKey: 'focus' },
  { name: 'Weight Management', slug: 'weight-loss-pack', price: 96, scoreKey: 'weight_loss' },
  { name: 'Longevity', slug: 'longevity-pack', price: 99, scoreKey: 'longevity' },
  { name: 'Liver Support', slug: 'liver-pack', price: 90, scoreKey: 'liver' }
];

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

function mapLabelToCode(label) {
  return LABEL_TO_CODE[label] || label;
}

function mapLabelsToCodesArray(labelsString) {
  if (!labelsString) return [];
  // Multi-select answers come as comma-separated
  return labelsString.split(',').map(label => mapLabelToCode(label.trim()));
}

function getUrlParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    first_name: params.get('first_name') || params.get('name') || '',
    email: params.get('email') || '',
    age_group: mapLabelToCode(params.get('age_group') || ''),
    energy_answer: mapLabelToCode(params.get('energy_answer') || ''),
    sleep_answer: mapLabelToCode(params.get('sleep_answer') || ''),
    stress_answer: mapLabelToCode(params.get('stress_answer') || ''),
    metabolism_answer: mapLabelToCode(params.get('metabolism_answer') || ''),
    exercise_answer: mapLabelToCode(params.get('exercise_answer') || ''),
    wellness_signals: mapLabelsToCodesArray(params.get('wellness_signals') || ''),
    contraindications: mapLabelsToCodesArray(params.get('contraindications') || ''),
    priority_answer: mapLabelToCode(params.get('priority_answer') || '')
  };
}

// =============================================================================
// SCORE INITIALIZATION
// =============================================================================

function initializeScores() {
  return {
    adrenal: 0,
    energy: 0,
    sleep: 0,
    blood_sugar: 0,
    gut: 0,
    mood: 0,
    mens_hormone: 0,
    performance: 0,
    muscle: 0,
    thyroid: 0,
    brain: 0,
    focus: 0,
    weight_loss: 0,
    longevity: 0,
    liver: 0
  };
}

function initializeFlags() {
  return {
    foundation_first: false,
    severity: 'normal',
    suppress_blood_sugar: false,
    suppress_thyroid: false,
    suppress_cardiovascular: false,
    caution_mood: false,
    defer_hormone: false,
    deferred_hormone_score: 0
  };
}

// =============================================================================
// SCORING FUNCTIONS (Mirror Python logic exactly)
// =============================================================================

function applyQ1Scoring(ageGroup, scores, flags) {
  // Age group affects hormone scoring thresholds
  // Handled in Q6 and Q7 with age_group parameter
}

function applyQ2Scoring(energyAnswer, scores) {
  if (energyAnswer === 'steady') {
    scores.adrenal -= 2;
    scores.energy -= 2;
    scores.thyroid -= 1;
  } else if (energyAnswer === 'needs_boost') {
    scores.adrenal += 3;
    scores.blood_sugar += 1;
    scores.thyroid += 1;
  } else if (energyAnswer === 'low') {
    scores.energy += 3;
    scores.adrenal += 2;
    scores.thyroid += 2;
  }
}

function applyQ3Scoring(sleepAnswer, scores) {
  if (sleepAnswer === 'rested') {
    scores.sleep -= 3;
    scores.mood -= 1;
  } else if (sleepAnswer === 'trouble_falling_asleep') {
    scores.sleep += 3;
    scores.mood += 2;
    scores.brain += 1;
  } else if (sleepAnswer === 'wake_during_night') {
    scores.blood_sugar += 3;
    scores.adrenal += 2;
    scores.sleep += 1;
  } else if (sleepAnswer === 'not_restored') {
    scores.sleep += 3;
    scores.adrenal += 1;
    scores.thyroid += 2;
  }
}

function applyQ4Scoring(stressAnswer, scores, flags) {
  if (stressAnswer === 'manages_well') {
    scores.adrenal -= 2;
    scores.mood -= 2;
  } else if (stressAnswer === 'occasional_tension') {
    scores.mood += 3;
    scores.adrenal += 1;
    scores.sleep += 1;
  } else if (stressAnswer === 'running_on_empty') {
    scores.adrenal += 3;
    scores.mood += 2;
    scores.gut += 1;
  } else if (stressAnswer === 'needs_support') {
    scores.adrenal += 4;
    scores.mood += 2;
    flags.severity = 'high';
  }
}

function applyQ5Scoring(metabolismAnswer, scores) {
  if (metabolismAnswer === 'stable') {
    scores.blood_sugar -= 2;
    scores.gut -= 2;
    scores.weight_loss -= 2;
  } else if (metabolismAnswer === 'midsection_changes') {
    scores.weight_loss += 3;
    scores.blood_sugar += 2;
    scores.mens_hormone += 1;
  } else if (metabolismAnswer === 'digestive_discomfort') {
    scores.gut += 4;
    scores.liver += 1;
  } else if (metabolismAnswer === 'energy_dips') {
    scores.blood_sugar += 4;
    scores.adrenal += 1;
  } else if (metabolismAnswer === 'multiple_issues') {
    scores.gut += 3;
    scores.blood_sugar += 3;
    scores.weight_loss += 2;
    scores.liver += 1;
  }
}

function applyQ6Scoring(exerciseAnswer, ageGroup, scores, flags) {
  if (exerciseAnswer === 'trains_regularly') {
    scores.performance += 3;
  } else if (exerciseAnswer === 'slow_recovery') {
    scores.muscle += 3;
    scores.mens_hormone += 1;
    if (ageGroup === '30s') {
      scores.mens_hormone += 2;
      scores.thyroid += 1;
    }
  } else if (exerciseAnswer === 'inconsistent') {
    flags.foundation_first = true;
  } else if (exerciseAnswer === 'energy_limited') {
    scores.adrenal += 2;
    scores.energy += 2;
    scores.thyroid += 1;
    flags.foundation_first = true;
  }
}

function applyQ7Scoring(wellnessSignals, ageGroup, scores) {
  if (wellnessSignals.includes('drive_changes')) {
    scores.mens_hormone += 2;
    scores.adrenal += 1;
    if (ageGroup === '30s') {
      scores.mens_hormone += 1;
    }
  }
  
  if (wellnessSignals.includes('body_composition')) {
    scores.mens_hormone += 2;
    scores.blood_sugar += 1;
  }
  
  if (wellnessSignals.includes('mental_fogginess')) {
    scores.brain += 3;
    scores.focus += 2;
    scores.thyroid += 1;
  }
  
  if (wellnessSignals.includes('feeling_cold')) {
    scores.thyroid += 3;
    scores.energy += 1;
  }
  
  if (wellnessSignals.includes('less_stamina')) {
    scores.mens_hormone += 2;
    scores.energy += 1;
  }
  
  if (wellnessSignals.includes('none')) {
    scores.mens_hormone -= 2;
    scores.brain -= 1;
    scores.thyroid -= 1;
  }
}

function applyQ8Scoring(contraindications, flags) {
  if (contraindications.includes('blood_sugar')) {
    flags.suppress_blood_sugar = true;
  }
  if (contraindications.includes('thyroid')) {
    flags.suppress_thyroid = true;
  }
  if (contraindications.includes('cardiovascular')) {
    flags.suppress_cardiovascular = true;
  }
  if (contraindications.includes('mood')) {
    flags.caution_mood = true;
  }
}

function applyQ10Scoring(priorityAnswer, scores) {
  if (priorityAnswer === 'energy') {
    scores.energy += 2;
    scores.adrenal += 2;
  } else if (priorityAnswer === 'sleep') {
    scores.sleep += 2;
    scores.mood += 1;
  } else if (priorityAnswer === 'strength') {
    scores.performance += 2;
    scores.muscle += 2;
    scores.mens_hormone += 1;
  } else if (priorityAnswer === 'metabolism') {
    scores.blood_sugar += 2;
    scores.weight_loss += 2;
    scores.gut += 1;
  } else if (priorityAnswer === 'clarity') {
    scores.brain += 3;
    scores.focus += 2;
  }
}

// =============================================================================
// HIERARCHY FILTERS
// =============================================================================

function applyHierarchyFilters(scores, flags) {
  // Rule 1: Contraindication Suppression
  if (flags.suppress_blood_sugar) {
    scores.blood_sugar = -99;
    scores.weight_loss -= 3; // Contains berberine
  }
  
  if (flags.suppress_thyroid) {
    scores.thyroid = -99;
    scores.adrenal -= 2; // Contains ashwagandha
    scores.energy -= 2;  // Contains ashwagandha
  }
  
  if (flags.suppress_cardiovascular) {
    scores.longevity = -99;
    scores.liver -= 2;
  }
  
  // Rule 2: Foundation First
  if (flags.foundation_first) {
    scores.performance = -99;
  }
  
  // Rule 4: Hormone Deferral
  if (scores.mens_hormone >= 5 && scores.adrenal >= 5) {
    flags.defer_hormone = true;
    flags.deferred_hormone_score = scores.mens_hormone;
    scores.mens_hormone = -50;
  }
  
  // Rule 5: Upstream Before Downstream
  if (scores.adrenal >= 5 || scores.energy >= 5) {
    scores.performance = Math.min(scores.performance, 2);
  }
}

// =============================================================================
// PACK SELECTION
// =============================================================================

function selectPacks(scores, flags) {
  // Build pack list with current scores
  const packScores = PACK_DEFINITIONS.map(pack => ({
    ...pack,
    score: scores[pack.scoreKey]
  }));
  
  // Sort by score descending
  packScores.sort((a, b) => b.score - a.score);
  
  // Handle deferred hormone pack
  let deferredPack = null;
  if (flags.defer_hormone) {
    deferredPack = { 
      name: "Men's Vitality", 
      slug: 'mens-hormone-pack', 
      price: 64,
      score: flags.deferred_hormone_score
    };
  }
  
  // Check if all scores are low (healthy profile)
  if (packScores.every(p => p.score < 3)) {
    return {
      primary: { name: 'Longevity', slug: 'longevity-pack', price: 99, score: 0 },
      secondary: null,
      tertiary: null,
      deferred: deferredPack
    };
  }
  
  // High severity = single pack only
  if (flags.severity === 'high') {
    const primary = packScores.find(p => p.score >= 5) || null;
    return {
      primary,
      secondary: null,
      tertiary: null,
      deferred: deferredPack
    };
  }
  
  // Normal selection
  const primary = packScores.find(p => p.score >= 5) || null;
  const secondary = packScores.find(p => p.score >= 4 && p !== primary) || null;
  const tertiary = packScores.find(p => p.score >= 6 && p !== primary && p !== secondary) || null;
  
  return { primary, secondary, tertiary, deferred: deferredPack };
}

// =============================================================================
// MAIN CALCULATION FUNCTION
// =============================================================================

function calculateResults() {
  const answers = getUrlParams();
  const scores = initializeScores();
  const flags = initializeFlags();
  
  // Apply all scoring
  applyQ1Scoring(answers.age_group, scores, flags);
  applyQ2Scoring(answers.energy_answer, scores);
  applyQ3Scoring(answers.sleep_answer, scores);
  applyQ4Scoring(answers.stress_answer, scores, flags);
  applyQ5Scoring(answers.metabolism_answer, scores);
  applyQ6Scoring(answers.exercise_answer, answers.age_group, scores, flags);
  applyQ7Scoring(answers.wellness_signals, answers.age_group, scores);
  applyQ8Scoring(answers.contraindications, flags);
  applyQ10Scoring(answers.priority_answer, scores);
  
  // Apply hierarchy filters
  applyHierarchyFilters(scores, flags);
  
  // Select packs
  const packs = selectPacks(scores, flags);
  
  // Calculate total
  let total = 0;
  if (packs.primary) total += packs.primary.price;
  if (packs.secondary) total += packs.secondary.price;
  if (packs.tertiary) total += packs.tertiary.price;
  
  return {
    firstName: answers.first_name,
    email: answers.email,
    scores,
    flags,
    packs,
    total,
    answers
  };
}

// =============================================================================
// SHOPIFY CART FUNCTIONS
// =============================================================================

async function addToCart(variantId, quantity = 1) {
  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        id: variantId,
        quantity: quantity
      })
    });
    return await response.json();
  } catch (error) {
    console.error('Add to cart error:', error);
    throw error;
  }
}

async function addMultipleToCart(items) {
  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ items })
    });
    return await response.json();
  } catch (error) {
    console.error('Add to cart error:', error);
    throw error;
  }
}

function redirectToCheckout() {
  window.location.href = '/checkout';
}

function redirectToCart() {
  window.location.href = '/cart';
}

// Export for use in page
window.ESEIS = {
  calculateResults,
  addToCart,
  addMultipleToCart,
  redirectToCheckout,
  redirectToCart,
  PACK_DEFINITIONS
};